import { NavLink, Outlet, Link } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Upload, Building2, Network, FileText, UserRound, Users, ArrowLeftRight, CreditCard, Lock, GitFork } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PeriodSelect } from "@/components/PeriodSelect";
import { DemoTour } from "@/components/DemoTour";
import { AvoraLogo } from "@/components/AvoraLogo";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/entities", label: "Entities", icon: Building2, id: "entities" },
  { to: "/uploads", label: "Uploads", icon: Upload, id: "uploads" },
  { to: "/ownership", label: "Ownership", icon: Network, id: "ownership" },
  { to: "/ownership-chart", label: "Ownership Chart", icon: GitFork, id: "ownership-chart" },
  { to: "/reports/entities", label: "Entity Valuations", icon: FileText, id: "entity-reports" },
  { to: "/reports/networth", label: "Net Worth Statements", icon: UserRound, id: "networth" },
  { to: "/comparison", label: "Month Comparison", icon: ArrowLeftRight, id: "comparison" },
];

const navFor = (me, usersLabel) => [
  ...NAV,
  ...(me?.role === "member" ? [] : [{ to: "/users", label: usersLabel, icon: Users, id: "users" }]),
  { to: "/billing", label: "Billing", icon: CreditCard, id: "billing" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors duration-200 ${
    isActive ? "bg-[#234E70] text-white shadow-[inset_3px_0_0_#C9A96E]" : "text-slate-300 hover:text-white hover:bg-white/5"
  }`;

function Sidebar({ me }) {
  const items = navFor(me, "Users");
  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#0D2B45] text-slate-100 min-h-screen sticky top-0 h-screen">
      <div className="px-6 pt-8 pb-10">
        <AvoraLogo dark sub="Intelligence Reporting Platform" />
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.id}`}>
            <n.icon className="h-4 w-4" strokeWidth={1.5} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-6 py-6 text-[11px] text-slate-500 font-mono border-t border-white/5">
        <div className="uppercase tracking-widest">Firm</div>
        <div className="text-slate-300 mt-1 truncate" data-testid="sidebar-firm-name">{me?.tenant_name}</div>
      </div>
    </aside>
  );
}

function MobileNav({ me }) {
  const items = navFor(me, "Users");
  return (
    <nav className="lg:hidden flex gap-1 overflow-x-auto px-4 py-2 bg-[#0D2B45]" data-testid="mobile-nav">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`mnav-${n.id}`}>
          <n.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span className="whitespace-nowrap">{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { me } = useApp();
  return (
    <div className="flex min-h-screen bg-[#F6F4EE]">
      <Sidebar me={me} />
      <div className="flex-1 min-w-0 flex flex-col">
        <MobileNav me={me} />
        <header className="sticky top-0 z-20 bg-[#F6F4EE]/85 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between gap-4 px-5 sm:px-10 py-3">
            <div className="flex items-center gap-4 sm:gap-6 min-w-0">
              <div className="hidden sm:block shrink-0" data-testid="header-brand">
                <AvoraLogo size="sm" sub="Asset Visualization Ownership Reporting Application" />
              </div>
              <div className="sm:hidden shrink-0" data-testid="header-brand-mobile"><AvoraLogo size="sm" /></div>
              <div className="hidden sm:block h-8 w-px bg-slate-300" />
              <div className="flex items-center gap-3 min-w-0">
                <span className="small-label hidden xl:inline">Month end</span>
                <PeriodSelect />
              </div>
            </div>
            <div className="flex items-center gap-4">
              {me?.demo && <DemoTour />}
              <span className="hidden md:inline text-xs font-mono text-slate-500 uppercase tracking-widest" data-testid="header-role">
                {me?.role}
              </span>
              <UserButton />
            </div>
          </div>
        </header>
        {me?.access && !me.access.can_edit && (
          <div className="bg-amber-50 border-b border-amber-200 px-5 sm:px-10 py-3 text-sm text-amber-900 flex items-center gap-3" data-testid="readonly-banner">
            <Lock className="h-4 w-4 shrink-0" />
            <span>View-only: your firm's subscription isn't active. Statements and PDFs still work; uploads and edits are paused.</span>
            <Link to="/billing" className="ml-auto underline whitespace-nowrap" data-testid="readonly-banner-billing">Billing</Link>
          </div>
        )}
        <main className="flex-1 px-5 sm:px-10 py-8 sm:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
